import { defineStore } from 'pinia'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant'
import EstateProjectAPI from '@/modules/estate-project/constants/estate-project-api-uri-const'
import { estateProjectMockAdapter } from '@/modules/estate-project/services/estate-project-mock-adapter'
import {
  formatEstateProjectToSubmit,
  mappingProjectToForm,
} from '@/modules/estate-project/helper/estate-project-form'
import type {
  EstateProject,
  EstateProjectFormValues,
  EstateProjectMedia,
} from '@/modules/estate-project/types/estate-project-types'

export enum EstateProjectDetailActionType {
  FETCHING = 'FETCHING',
  FETCHED = 'FETCHED',
  FETCH_FAILED = 'FETCH_FAILED',
  UPDATING = 'UPDATING',
  UPDATED = 'UPDATED',
  UPDATE_FAILED = 'UPDATE_FAILED',
  DELETING = 'DELETING',
  DELETED = 'DELETED',
  DELETE_FAILED = 'DELETE_FAILED',
}

interface EstateProjectDetailRecord {
  action_type: EstateProjectDetailActionType
  action_message: string
  data: EstateProject | null
  formData: ReturnType<typeof mappingProjectToForm> | null
}

interface EstateProjectDetailState {
  projects: Record<number, EstateProjectDetailRecord>
}

const toRecord = (value: unknown): Record<string, unknown> => {
  if (typeof value === 'object' && value !== null) return value as Record<string, unknown>
  return {}
}

const toMedia = (input: unknown): EstateProjectMedia | null => {
  if (typeof input === 'string' && input) return { uuid: input }
  const source = toRecord(input)
  if (!source.uuid) return null
  return {
    uuid: String(source.uuid),
    name: source.name
      ? String(source.name)
      : source.filename
        ? String(source.filename)
        : source.original_filename
          ? String(source.original_filename)
          : undefined,
    url:
      typeof source.url === 'object' && source.url !== null
        ? (source.url as EstateProjectMedia['url'])
        : undefined,
  }
}

const normalizeMediaList = (input: unknown): EstateProjectMedia[] => {
  if (!Array.isArray(input)) return []
  return input
    .map((item) => toMedia(item))
    .filter((item): item is EstateProjectMedia => !!item?.uuid)
}

const normalizePolygonGeoJson = (input: unknown) => {
  const source = toRecord(input)
  const type = String(source.type || '').toLowerCase()
  const coordinates = source.coordinates

  if (type === 'polygon' && Array.isArray(coordinates) && Array.isArray(coordinates[0])) {
    return {
      type: 'Polygon' as const,
      coordinates: coordinates as number[][][],
    }
  }

  if (type === 'multipolygon' && Array.isArray(coordinates)) {
    const firstPolygon = coordinates[0]
    if (Array.isArray(firstPolygon) && Array.isArray(firstPolygon[0])) {
      return {
        type: 'Polygon' as const,
        coordinates: firstPolygon as number[][][],
      }
    }
  }

  return null
}

const normalizeDetailPayload = (payload: unknown): EstateProject => {
  const safePayload = toRecord(payload)
  const root = toRecord(safePayload.data)
  const nested = toRecord(root.data)
  const source = nested.id ? nested : root.id ? root : safePayload

  const id = Number(source.id)
  const name = String(source.project_name || source.name || '').trim()
  const address = String(source.project_address || source.address || '').trim()
  const lat = Number(source.lat)
  const lng = Number(source.lng)

  const polygon =
    normalizePolygonGeoJson(source.polygon_geojson) ||
    normalizePolygonGeoJson(source.geojson) ||
    normalizePolygonGeoJson(source.geom_4326)

  const galleries = toRecord(source.galleries)

  if (
    !Number.isFinite(id) ||
    id <= 0 ||
    !name ||
    !address ||
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    !polygon
  ) {
    throw new Error('Dữ liệu chi tiết dự án không hợp lệ từ API')
  }

  const photos = normalizeMediaList(
    Array.isArray(source.photo_details)
      ? source.photo_details
      : Array.isArray(galleries.photos)
        ? galleries.photos
        : source.photos,
  )

  const videos = normalizeMediaList(
    Array.isArray(source.video_details)
      ? source.video_details
      : Array.isArray(galleries.videos)
        ? galleries.videos
        : source.videos,
  )

  const information = Array.isArray(source.information)
    ? source.information
    : Array.isArray(source.project_information)
      ? source.project_information
      : []

  return {
    ...(source as any),
    id,
    project_name: name,
    project_address: address,
    name: source.name ? String(source.name) : undefined,
    address: source.address ? String(source.address) : undefined,
    lat,
    lng,
    polygon_geojson: polygon as any,
    geojson: polygon as any,
    investor: (source.investor || source.owner || null) as any,
    owner: (source.owner || source.investor || null) as any,
    project_information: information as any,
    information: information as any,
    description: String(source.description || ''),
    photos,
    videos,
    logo: (toMedia(source.logo_details) || toMedia(source.logo) || null) as any,
    status: (source.status || 'active') as any,
    created_at: String(source.created_at || ''),
    updated_at: String(source.updated_at || ''),
    created_by_name: String(source.created_by_name || toRecord(source.owner).name || ''),
  }
}

export const useEstateProjectDetailStore = defineStore('estate-project.detail', {
  state: (): EstateProjectDetailState => ({
    projects: {},
  }),
  actions: {
    async fetchDetail(id: number) {
      this.projects[id] = {
        ...this.projects[id],
        action_type: EstateProjectDetailActionType.FETCHING,
        action_message: '',
        data: this.projects[id]?.data || null,
        formData: this.projects[id]?.formData || null,
      }

      try {
        const res = await APIClient.get(
          EstateProjectAPI.project.detail.replace(':id', id.toString()),
        )
        const data = normalizeDetailPayload(res)
        this.projects[id] = {
          action_type: EstateProjectDetailActionType.FETCHED,
          action_message: '',
          data,
          formData: mappingProjectToForm(data),
        }
        return data
      } catch {
        this.projects[id] = {
          ...this.projects[id],
          action_type: EstateProjectDetailActionType.FETCH_FAILED,
          action_message: 'Không thể tải chi tiết dự án',
        }
        throw new Error('Không thể tải chi tiết dự án')
      }
    },

    async updateProject(id: number, values: EstateProjectFormValues) {
      if (!this.projects[id]) {
        throw new Error('Không tìm thấy dữ liệu dự án')
      }

      this.projects[id].action_type = EstateProjectDetailActionType.UPDATING
      this.projects[id].action_message = ''

      try {
        const payload = formatEstateProjectToSubmit(values)
        const res = await APIClient.put(
          EstateProjectAPI.project.update.replace(':id', id.toString()),
          payload,
        )
        const updated = normalizeDetailPayload(res)

        this.projects[id] = {
          action_type: EstateProjectDetailActionType.UPDATED,
          action_message: '',
          data: updated,
          formData: mappingProjectToForm(updated),
        }

        return updated
      } catch {
        this.projects[id].action_type = EstateProjectDetailActionType.UPDATE_FAILED
        this.projects[id].action_message = 'Không thể cập nhật dự án'
        throw new Error('Không thể cập nhật dự án')
      }
    },

    async deleteProject(id: number) {
      if (!this.projects[id]) {
        throw new Error('Không tìm thấy dữ liệu dự án')
      }

      this.projects[id].action_type = EstateProjectDetailActionType.DELETING
      this.projects[id].action_message = ''

      try {
        const deleted = await estateProjectMockAdapter.deleteProject(id)
        this.projects[id] = {
          action_type: EstateProjectDetailActionType.DELETED,
          action_message: '',
          data: null,
          formData: null,
        }
        return deleted
      } catch {
        this.projects[id].action_type = EstateProjectDetailActionType.DELETE_FAILED
        this.projects[id].action_message = 'Không thể xoá dự án'
        throw new Error('Không thể xoá dự án')
      }
    },
  },
})
